/**
 * 智能分级日志工具
 *
 * 功能：
 * 1. 默认：只有 ERROR 级别日志立即显示，其他缓冲
 * 2. 出错时：自动输出之前缓冲的所有日志 + 错误日志
 * 3. REACT_APP_ENABLE_LOG=true：始终显示所有日志
 * 4. REACT_APP_LOG_LEVEL：设置最低显示级别 (error/warn/info/debug)
 *
 * 使用方式：
 * import logger from '../utils/logger';
 * logger.debug('调试信息');  // 默认不显示，出错时显示
 * logger.info('普通信息');   // 默认不显示，出错时显示
 * logger.warn('警告信息');    // 默认不显示，出错时显示
 * logger.error('错误信息');  // 立即显示，触发缓冲日志输出
 */

const isDev = process.env.NODE_ENV === 'development';
const enableLogEnv = process.env.REACT_APP_ENABLE_LOG === 'true';

// 最低显示级别配置 (error=3, warn=2, info=1, debug=0)
// 只有 >= 此级别的日志才会显示
const LOG_LEVEL = (() => {
  const envLevel = process.env.REACT_APP_LOG_LEVEL?.toUpperCase();
  const levelMap = { ERROR: 3, WARN: 2, INFO: 1, DEBUG: 0 };
  return levelMap[envLevel] ?? 3; // 默认只显示 ERROR
})();

// 日志级别
const LEVELS = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3,
};

// 日志缓冲（用于错误时回溯）
let logBuffer = [];
const MAX_BUFFER_SIZE = 100;

// 是否已发生错误（已触发自动启日志）
let errorOccurred = false;

// 原始 console 方法引用
const originalConsole = {
  log: console.log.bind(console),
  debug: console.debug.bind(console),
  info: console.info.bind(console),
  warn: console.warn.bind(console),
  error: console.error.bind(console),
};

/**
 * 自动启用日志并输出缓冲内容
 */
const flushBuffer = () => {
  if (errorOccurred && logBuffer.length > 0) {
    originalConsole.log('📋 [Logger] 错误发生前的日志记录:');
    logBuffer.forEach(({ level, args, timestamp }) => {
      const timeStr = new Date(timestamp).toLocaleTimeString();
      const levelTag = `[${level}]`;
      switch (level) {
        case 'ERROR':
          originalConsole.error(`  ${timeStr} ${levelTag}`, ...args);
          break;
        case 'WARN':
          originalConsole.warn(`  ${timeStr} ${levelTag}`, ...args);
          break;
        default:
          originalConsole.log(`  ${timeStr} ${levelTag}`, ...args);
      }
    });
    originalConsole.log('📋 [Logger] 日志记录结束');
    logBuffer = [];
  }
};

/**
 * 触发自动启日志（发生错误时调用）
 */
const triggerAutoEnable = () => {
  if (!errorOccurred) {
    errorOccurred = true;
    flushBuffer();
  }
};

/**
 * 统一的日志处理函数
 */
const log = (levelName, ...args) => {
  const levelValue = LEVELS[levelName] ?? 0;

  // 是否应该直接输出（开发环境/强制启用日志/达到最低显示级别）
  const shouldOutput = isDev || enableLogEnv || levelValue >= LOG_LEVEL;

  if (shouldOutput) {
    // 开发环境、启用日志环境变量或达到最低显示级别：直接输出
    outputToConsole(levelName, ...args);
  } else if (errorOccurred) {
    // 错误已发生，输出（即使未达到最低级别）
    outputToConsole(levelName, ...args);
  } else {
    // 正常状态：缓冲日志（等待错误发生或手动启用）
    logBuffer.push({
      level: levelName,
      args,
      timestamp: Date.now(),
    });
    if (logBuffer.length > MAX_BUFFER_SIZE) {
      logBuffer.shift();
    }
  }
};

/**
 * 输出到控制台
 */
const outputToConsole = (levelName, ...args) => {
  const levelTag = `[${levelName}]`;
  switch (levelName) {
    case 'ERROR':
      originalConsole.error(levelTag, ...args);
      break;
    case 'WARN':
      originalConsole.warn(levelTag, ...args);
      break;
    case 'INFO':
      originalConsole.info(levelTag, ...args);
      break;
    default:
      originalConsole.log(levelTag, ...args);
  }
};

// 自动监听全局错误
if (typeof window !== 'undefined') {
  window.addEventListener('error', (event) => {
    triggerAutoEnable();
    originalConsole.error('🚨 [Global Error]', event.error);
  });

  window.addEventListener('unhandledrejection', (event) => {
    triggerAutoEnable();
    originalConsole.error('🚨 [Unhandled Promise Rejection]', event.reason);
  });
}

// 对外暴露的 logger 对象
const logger = {
  debug: (...args) => log('DEBUG', ...args),
  info: (...args) => log('INFO', ...args),
  warn: (...args) => log('WARN', ...args),
  error: (...args) => log('ERROR', ...args),

  // 手动启用日志（用于调试）
  enable: () => {
    errorOccurred = true;
    flushBuffer();
  },

  // 重置状态
  reset: () => {
    errorOccurred = false;
    logBuffer = [];
  },

  // 获取缓冲状态（用于调试）
  getStatus: () => ({
    errorOccurred,
    bufferSize: logBuffer.length,
    isDev,
    enableLogEnv,
  }),
};

export default logger;
